import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';

/** Keeps the destination through registration and email confirmation in this browser. */
@Injectable({providedIn: 'root'})
export class AuthReturnUrlService {
  private readonly storageKey = 'wtg.auth.returnUrl';
  private pendingUrl: string | null = null;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: object) {}

  public remember(value: unknown): void {
    if (!this.isLocalUrl(value)) {
      return;
    }
    this.pendingUrl = value;
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.setItem(this.storageKey, value);
      } catch {
        // Navigation still works when browser storage is unavailable.
      }
    }
  }

  public resolve(value?: unknown): string {
    if (this.isLocalUrl(value)) {
      return value;
    }
    let stored = this.pendingUrl;
    if (!stored && isPlatformBrowser(this.platformId)) {
      try {
        stored = localStorage.getItem(this.storageKey);
      } catch {
        // Fall back to the in-memory destination or the home page.
      }
    }
    return this.isLocalUrl(stored) ? stored : '/';
  }

  public consume(value?: unknown): string {
    const destination = this.resolve(value);
    this.pendingUrl = null;
    if (isPlatformBrowser(this.platformId)) {
      try {
        localStorage.removeItem(this.storageKey);
      } catch {
        // Storage can be disabled independently of authentication.
      }
    }
    return destination;
  }

  private isLocalUrl(value: unknown): value is string {
    return typeof value === 'string' && value.startsWith('/')
      && !value.startsWith('//') && !/[\\\u0000-\u0020\u007f]/.test(value);
  }
}
